import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useGetTipMetadataExcelQuery } from "../../libs/redux/slices/Trkgfile/trkgfileApislice";
import { useGetPuertoByPaisQuery } from "../../libs/redux/slices/Puertosx/puertosxApiSlice";
import { getNombresPuertos } from "../../libs/redux/slices/Puertosx/puertosxSlice";
import { describirPuerto } from "./puertos";

/** Funcion de Genio (GEST0004.METOPXXX) que lista los puertos de Colombia */
export const FUNCION_PUERTOS_COL = "f_getPuertoCol";

const FILTRO_METADATOS_PUERTO = {
  filters: `filters[REGESTXX]=ACTIVO&filters[METOPXXX]=${FUNCION_PUERTOS_COL}`,
};

/**
 * Descripcion de los metadatos de puertos de Colombia para el visor.
 *
 * Los metadatos se reconocen por la funcion (METOPXXX = f_getPuertoCol) y no
 * por su METIDXXX, porque la misma funcion se puede usar en varios metadatos.
 * La consulta de documentos no cambia: la lista de metadatos de la funcion y la
 * de puertos se piden aparte con RTK Query, que las deja en cache para toda la
 * sesion, y la de puertos solo si algun documento cargado tiene uno de esos
 * metadatos. Los nombres quedan en el slice puertosx.
 *
 * @param {Array<object>} rows - documentos cargados en el visor
 * @returns {{describirMetadato: function(string, *): (string|null), etiquetasPuerto: Map<string, string>}}
 *   describirMetadato devuelve CODIGO - NOMBRE, o null si la clave no es de
 *   puertos o los nombres aun no estan; etiquetasPuerto es METIDXXX -> METDESXX
 */
export const usePuertosCol = (rows = []) => {
  const { data } = useGetTipMetadataExcelQuery(FILTRO_METADATOS_PUERTO);
  const nombresPorCodigo = useSelector(getNombresPuertos);

  const etiquetasPuerto = useMemo(
    () => new Map((data?.data ?? []).map((metadato) => [metadato.METIDXXX, metadato.METDESXX])),
    [data]
  );

  const hayPuertos = useMemo(
    () =>
      etiquetasPuerto.size > 0 &&
      rows.some((row) =>
        Object.keys(row?.JSONMETX || {}).some((clave) => etiquetasPuerto.has(clave))
      ),
    [rows, etiquetasPuerto]
  );

  useGetPuertoByPaisQuery({ PAIIDXXX: "CO" }, { skip: !hayPuertos });

  const describirMetadato = useCallback(
    (clave, valor) => {
      if (!etiquetasPuerto.has(clave) || Object.keys(nombresPorCodigo).length === 0) return null;
      return describirPuerto(valor, nombresPorCodigo);
    },
    [nombresPorCodigo, etiquetasPuerto]
  );

  return { describirMetadato, etiquetasPuerto };
};
